import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import Alert from "src/components-v2/Alert";
import Button from "src/components-v2/Button";
import FieldGroup from "src/components-v2/FieldGroup";
import FieldLabel from "src/components-v2/FieldLabel";
import RadioButton from "src/components-v2/RadioButton";
import Link from "src/components/Link";
import { StyledModal } from "./index.styled";

import { OTP_METHOD } from "src/consts/transactions";
import { useUserInfo } from "src/hooks/user";
import privateRoutes from "src/routes/private-routes";
import { useDemo } from "src/scenes/Sandbox/hooks/useCoachmark";

export type SendOtpModalProps = {
  /**
   * Set user email
   */
  email?: string;

  /**
   * Set user email for not owner
   */
  emailNonOwner?: string;

  /**
   * Set user phone number
   */
  phoneNumber?: string | number;

  /**
   * Show or hide modal
   */
  showOtp: boolean;

  /**
   * Set selected OTP method
   */
  otp: string | number;

  /**
   * OTP method radio button handler
   */
  onSetOtp: Function;

  /**
   * Modal toggle
   */
  toggleOtp: Function;

  /**
   * Submit handler
   */
  onClickDeposit: Function;

  /**
   * Set if submit disabled
   */
  isDisabled: boolean;

  /**
   * Flag to display the old OTP method
   * that still used by Payment pod
   */
  isOldVersion?: boolean;

  /**
   * Flag to display loading indicator
   */
  isSubmitting?: boolean;

  isReselecting?: boolean;

  /**
   * additive channel filter so a caller
   * can hide the GAuth (`OTP_METHOD.GAUTH`, code 5) radio option regardless of `isGAuthActive`.
   */
  hideGauthMethod?: boolean;
};

function SendOtpModal(props: SendOtpModalProps) {
  const {
    showOtp,
    otp,
    onSetOtp,
    toggleOtp,
    onClickDeposit,
    isDisabled,
    isOldVersion,
    isReselecting,
    isSubmitting,
    hideGauthMethod,
    ...restProps
  } = props;
  const { t } = useTranslation(["transaction", "errors"]);
  const { isDemo } = useDemo();
  const location = useLocation();
  const { user } = useUserInfo();
  const [tempSelectedMethod, setTempSelectedMethod] = useState<string | number>("");

  const isInSummaryPaymentPage = location.hash === privateRoutes.sandboxTransactionSummaryTransaction.path;
  const isEmailNonOwnerSelected = tempSelectedMethod === OTP_METHOD.EMAIL_NON_OWNER;
  const isEmailSelected = tempSelectedMethod === OTP_METHOD.EMAIL;
  const isPhoneSelected = tempSelectedMethod === OTP_METHOD.PHONE_NUMBER;
  const isPhoneWASelected = tempSelectedMethod === OTP_METHOD.COMPANY_PHONE_NUMBER_WA;
  const isUserPhoneSelected = tempSelectedMethod === OTP_METHOD.USER_PHONE_NUMBER;
  const isUserPhoneWASelected = tempSelectedMethod === OTP_METHOD.USER_PHONE_NUMBER_WA;
  const isGAuthSelected = tempSelectedMethod === OTP_METHOD.GAUTH;
  const isGAuthActive = user?.tfa_status === "active";
  // `hideGauthMethod` governs whether GAuth renders as an OPTION -- gating on real
  // activation state as before, but never when the caller has hidden it.
  const showGauthOption = isGAuthActive && !hideGauthMethod;
  // ...and separately, whether GAuth is a "missing setup" CONCERN worth nudging the user about in
  // the info alert below. When `hideGauthMethod` is set, GAuth is never offered on this flow, so
  // its activation state stops being something the user can act on here -- `isGauthMissing` is
  // forced `false` rather than reusing `!showGauthOption` (which would incorrectly fire the nudge
  // for every hidden+inactive user). Every existing caller omits the prop, so this reduces to
  // `!isGAuthActive` for them -- unchanged.
  const isGauthMissing = !hideGauthMethod && !isGAuthActive;
  const isUserPhoneExist = !!user?.current_user?.phone_number;
  const isShowAlertInfo = isGauthMissing || !isUserPhoneExist;

  useEffect(() => {
    if (!!otp) {
      setTempSelectedMethod(otp);
    }
  }, [otp]);

  function onCloseModal() {
    if (isDemo && isInSummaryPaymentPage) return;
    toggleOtp(false);
  }

  function onSubmit() {
    if (otp === tempSelectedMethod && isGAuthSelected && isReselecting) {
      return toggleOtp(false);
    }
    onClickDeposit(tempSelectedMethod);
  }

  function onSelectMethod(selectedMethod: string | number) {
    if (!isReselecting) {
      return onSetOtp(selectedMethod);
    }
    setTempSelectedMethod(selectedMethod);
  }

  return (
    <StyledModal
      title={t("summary.modal.otp")}
      show={showOtp}
      onClose={onCloseModal}
      closeOnOverlay
      closeIcon={!isOldVersion}
      size="small"
      {...restProps}
    >
      {isOldVersion ? (
        <>
          <FieldGroup className="u-border-b u-border-dashed u-border-neutral_border">
            <FieldLabel className="u-mb-2">{t("summary.modal.non-owner.email")}</FieldLabel>
            <RadioButton
              name="radioOtp"
              checked={isEmailNonOwnerSelected}
              onChange={() => onSelectMethod(OTP_METHOD.EMAIL_NON_OWNER)}
              data-qaid="qa-checked-email-otp"
            >
              <p className="u-font-bold u-mb-0">
                <span className="u-font-normal">{` (${user?.current_user?.email})`}</span>
              </p>
            </RadioButton>
          </FieldGroup>
          <FieldGroup className="u-mt-3">
            <FieldLabel className="u-mb-2">{t("summary.modal.non-owner.phone")}</FieldLabel>
            <RadioButton
              name="radioOtp"
              checked={isEmailSelected}
              onChange={() => onSelectMethod(OTP_METHOD.EMAIL)}
              data-qaid="qa-checked-email-otp"
              className="u-w-full"
            >
              <p className="u-font-bold u-mb-0">
                <span className="u-font-normal">{` (${user?.company?.email})`}</span>
              </p>
            </RadioButton>
            <RadioButton
              name="radioOtp"
              checked={isPhoneSelected}
              onChange={() => onSelectMethod(OTP_METHOD.PHONE_NUMBER)}
              data-qaid="qa-checked-phone-otp"
              className="u-w-full"
              disabled={!isDemo && !user?.company?.phone_number}
            >
              <p className="coba-highlight u-font-bold u-mb-0">
                <span className="u-font-normal">{` (${
                  !!user?.company?.phone_number ? user?.company?.phone_number : isDemo ? "081234566xxx" : "-"
                })`}</span>
              </p>
            </RadioButton>
          </FieldGroup>
        </>
      ) : (
        <>
          {showGauthOption && (
            <RadioButton
              checked={isGAuthSelected}
              onChange={() => onSelectMethod(OTP_METHOD.GAUTH)}
              disabled={isDisabled}
              className={classNames(
                "u-flex u-items-center u-border u-border-gray u-rounded-lg u-p-2 u-mb-2",
                isGAuthSelected && "u-border-flip-orange u-bg-light-anemia u-text-brick-h u-font-bold"
              )}
            >
              <div className={classNames(isGAuthSelected && "u-text-brick-h u-font-bold", "u-ml-2")}>
                <div className="u-font-bold">
                  <Trans t={t} i18nKey={"summary.modal.gauth"} />
                </div>
                <div className={classNames("u-font-medium u-text-semi")}>{t("summary.modal.gauth-desc")}</div>
              </div>
            </RadioButton>
          )}

          {user?.current_user?.phone_number && (
            <RadioButton
              onChange={() => onSelectMethod(OTP_METHOD.USER_PHONE_NUMBER_WA)}
              disabled={isDisabled}
              className={classNames(
                "u-flex u-items-center u-border u-border-gray u-rounded-lg u-p-2 u-mb-2",
                isUserPhoneSelected ||
                  (isUserPhoneWASelected && "u-border-flip-orange u-bg-light-anemia u-text-brick-h u-font-bold")
              )}
              checked={isUserPhoneWASelected}
            >
              <div className={classNames(isUserPhoneSelected && "u-text-brick-h u-font-bold", "u-ml-2")}>
                <div className="u-font-bold">
                  <Trans t={t} i18nKey={"summary.modal.user-phone"}>
                    <span
                      className={classNames(
                        "u-font-medium u-text-semi u-italic",
                        !isUserPhoneSelected || (!isUserPhoneWASelected && "u-text-neutral_secondary_text")
                      )}
                    />
                  </Trans>
                </div>
                <div className={classNames("u-font-medium")}>{user?.current_user?.phone_number}</div>
              </div>
            </RadioButton>
          )}

          <div className="u-mb-2">
            <RadioButton
              checked={isPhoneWASelected}
              onChange={() => onSelectMethod(OTP_METHOD.COMPANY_PHONE_NUMBER_WA)}
              disabled={isDisabled}
              className={classNames(
                "u-flex u-items-center u-border u-border-gray u-rounded-lg u-p-2 u-mb-2",
                isPhoneSelected ||
                  (isPhoneWASelected && "u-border-flip-orange u-bg-light-anemia u-text-brick-h u-font-bold")
              )}
            >
              <div
                className={classNames(isPhoneSelected || (isPhoneWASelected && "u-text-brick-h u-font-bold"), "u-ml-2")}
              >
                <div className="u-font-bold">
                  <Trans t={t} i18nKey={"summary.modal.phone"} />
                </div>
                <div className={classNames("u-font-medium")}>{user?.company?.phone_number}</div>
              </div>
            </RadioButton>
          </div>

          {isShowAlertInfo && (
            <Alert className="u-text-semi u-text-neutral_main_text u-font-medium" color="grey" icon={faInfoCircle}>
              {isGauthMissing && isUserPhoneExist && (
                <span>
                  <Trans i18nKey={"summary.modal.gauth-is-not-setup"} t={t}>
                    <Link
                      className="u-text-flip-orange u-no-underline"
                      to={privateRoutes.settingsTFA.path}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  </Trans>
                </span>
              )}

              {!isGauthMissing && !isUserPhoneExist && <Trans>{t("summary.modal.phone-not-registered")}</Trans>}

              {isGauthMissing && !isUserPhoneExist && (
                <ul className="u-pl-5">
                  <li>
                    <Trans>{t("summary.modal.phone-not-registered")}</Trans>
                  </li>
                  <li>
                    <Trans i18nKey={"summary.modal.gauth-is-not-setup"} t={t}>
                      <Link className="u-text-flip-orange u-no-underline" to={privateRoutes.settingsTFA.path} />
                    </Trans>
                  </li>
                </ul>
              )}
            </Alert>
          )}
        </>
      )}

      <Button
        className="u-mt-4"
        variant="secondary"
        block
        onClick={onSubmit}
        disabled={isDisabled || (otp === "" && tempSelectedMethod === "")}
        data-qaid="qa-button-continue-otp"
        activated={isSubmitting}
      >
        {t("summary.modal.button-submit")}
      </Button>
    </StyledModal>
  );
}

export default SendOtpModal;
